import { StickyNote as StickyNoteIcon } from 'lucide-react';
import { toast } from 'sonner';
import DraggableIcon from '../../DraggableIcon';
import { pdfCoordsToPixels, PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT } from '../utils/pdfCoordinates';

export default function PageNotesOverlay({
  pageNum,
  pageRefs,
  pageDimensions,
  pageNotes,
  highlightedNoteId,
  positionUpdateTrigger,
  onNoteUpdate,
  onNotePageChange,
  containerRef,
  setEditingNote,
  numPages,
}) {
  return pageNotes.map((note) => {
    const pageContainer = pageRefs.current[pageNum];
    const pageElement = pageContainer?.querySelector('.react-pdf__Page');

    if (!note.position || typeof note.position !== 'object' || note.position.x === undefined || note.position.y === undefined) {
      console.warn('⚠️ Note missing position data (should have been filtered):', {
        id: note.id,
        page_number: note.page_number,
        hasPosition: !!note.position,
        position: note.position
      });
      return null;
    }

    let x = 0;
    let y = 0;

    if (pageElement) {
      const rect = pageElement.getBoundingClientRect();
      const pos = pdfCoordsToPixels({
        x: note.position.x,
        y: note.position.y,
        width: rect.width,
        height: rect.height,
      });
      x = pos.x;
      y = pos.y;
    } else if (pageDimensions.current[pageNum]) {
      const dims = pageDimensions.current[pageNum];
      const pos = pdfCoordsToPixels({
        x: note.position.x,
        y: note.position.y,
        width: dims.width,
        height: dims.height,
      });
      x = pos.x;
      y = pos.y;
    } else {
      const estimatedWidth = 800;
      const estimatedHeight = estimatedWidth * (PDF_PAGE_HEIGHT / PDF_PAGE_WIDTH);
      const pos = pdfCoordsToPixels({
        x: note.position.x,
        y: note.position.y,
        width: estimatedWidth,
        height: estimatedHeight,
      });
      x = pos.x;
      y = pos.y;
    }

    const isHighlighted = highlightedNoteId === note.id;
    const isTempNote = note.id && note.id.toString().startsWith('temp-');

    const handleNoteDragEnd = async (positionTop, positionLeft) => {
      if (isTempNote || !onNoteUpdate) return;

      try {
        if (pageElement) {
          const rect = pageElement.getBoundingClientRect();
          const pixelX = (positionLeft / 100) * rect.width;
          const pixelY = (positionTop / 100) * rect.height;

          const positionX = (pixelX / rect.width) * PDF_PAGE_WIDTH;
          const positionY = (pixelY / rect.height) * PDF_PAGE_HEIGHT;

          if (onNotePageChange && containerRef?.current) {
            const pageRect = pageContainer.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            const absoluteY = (pageRect.top - containerRect.top) + pixelY + (containerRef.current.scrollTop || 0);

            let accumulatedHeight = 0;
            let targetPage = note.page_number;

            for (let i = 1; i <= numPages; i++) {
              const pageRef = pageRefs.current[i];
              if (pageRef) {
                const pageHeight = pageRef.offsetHeight;
                if (absoluteY >= accumulatedHeight && absoluteY < accumulatedHeight + pageHeight) {
                  targetPage = i;
                  break;
                }
                accumulatedHeight += pageHeight;
              }
            }

            if (targetPage !== note.page_number) {
              console.log(`📄 Note dragged from page ${note.page_number} to page ${targetPage}`);
              toast.success(`Note moved to page ${targetPage}`);
              await onNotePageChange(note.id, targetPage);
              return;
            }
          }

          await onNoteUpdate(note.id, {
            position_x: positionX,
            position_y: positionY
          });
        }
      } catch (error) {
        console.error('Error saving note position:', error);
        toast.error('Failed to save note position');
      }
    };

    return (
      <DraggableIcon
        key={`${note.id}-${positionUpdateTrigger}`}
        initialLeft={x}
        initialTop={y}
        onDragEnd={handleNoteDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          setEditingNote(note);
        }}
        pageElement={pageElement}
        containerElement={containerRef?.current}
        disabled={isTempNote}
        className={isHighlighted ? 'z-30' : 'z-10'}
      >
        <div className="relative">
          <div
            className={`w-10 h-10 rounded-full border-2 border-white shadow-lg cursor-pointer transition-all hover:scale-110 flex items-center justify-center ${isHighlighted
              ? 'ring-4 ring-yellow-400 ring-opacity-90 shadow-2xl scale-125'
              : '',
              isTempNote ? 'opacity-70 animate-pulse' : '',
              note.color === 'yellow' ? 'bg-yellow-400' :
                note.color === 'blue' ? 'bg-blue-400' :
                  note.color === 'green' ? 'bg-green-400' :
                    note.color === 'pink' ? 'bg-pink-400' :
                      note.color === 'purple' ? 'bg-purple-400' :
                        'bg-yellow-400'
              }`}
            style={isHighlighted ? {
              boxShadow: '0 0 20px rgba(255, 193, 7, 0.9), 0 0 40px rgba(255, 193, 7, 0.6), 0 0 60px rgba(255, 193, 7, 0.3)',
              animation: 'pulse 0.8s ease-in-out infinite, ping 1s cubic-bezier(0, 0, 0.2, 1) infinite'
            } : {}}
            title={note.heading || note.content || 'Click to edit or drag to move'}
          >
            <StickyNoteIcon className={`w-6 h-6 text-white ${isHighlighted ? 'drop-shadow-lg' : ''}`} />
          </div>
          {isHighlighted && (
            <div
              className="absolute inset-0 rounded-full border-4 border-yellow-400 pointer-events-none"
              style={{
                animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
                opacity: 0.6,
                margin: '-4px'
              }}
            />
          )}
        </div>
      </DraggableIcon>
    );
  });
}
